/**
 * 云服务类别 产品图表页 图表详情Dialog
 *
 * @format
 */

import React, { useEffect, useState } from 'react'
import { _$, Button, Dialog, Select, OptionGroup, Option } from 'kpc-react'
import moment from 'moment'
import styled from 'styled-components'
import NoDataComponent from 'src/components/NoData'
import QuickRangeDate from 'src/components/QuickRangeDate'
import BaseChart from 'src/components/BaseChart'
import { request, tooltipFormatter, handleUnit } from 'src/utils'
import {
  calcDateFormat,
  IChartDataProps,
  calcSelectInterval,
  getQueryDataParams
} from '../constants'
import API from 'src/api/ApiCloud'
interface IDiabolChartDetailsProps {
  visible: boolean
  region: string
  productType: number
  graph: { instanceId?: string; graphId?: number; units?: string; title?: string }
  closeDetails: () => void
  rangeDate: [number, number]
  quickDate: string
  mointorClassData: any[]
  className?: string
}

const DiabolChartDetails: React.FC<IDiabolChartDetailsProps> = ({
  visible,
  region,
  productType,
  graph,
  closeDetails,
  rangeDate: rangeDateProp,
  quickDate: quickDateProp,
  mointorClassData,
  className
}) => {
  const [monitorItemValue, setMonitorItemValue] = useState<string>()
  const [quickDate, setQuickDate] = useState<string>()
  const [rangeDate, setRangeDate] = useState([])
  const [delayOptions, setDelayOptions] = useState<string[]>([])
  const [delayValue, setDelayValue] = useState<string>('')
  const [currentGraph, setCurrentGraph] = useState<any>({})
  const [chartData, setChartData] = useState<IChartDataProps>()
  // 初始化
  useEffect(() => {
    if (visible && graph.graphId !== undefined && graph.title) {
      setMonitorItemValue(`${graph.graphId}-${graph.title}`)
      quickDateProp ? setQuickDate(quickDateProp) : rangeDateChange(rangeDateProp)
    } else {
      setQuickDate(undefined)
      setRangeDate([])
      setMonitorItemValue(undefined)
      setDelayOptions([])
      setDelayValue('')
      setCurrentGraph(undefined)
      setChartData(undefined)
    }
  }, [visible, graph.graphId, graph.graphId])

  // 当监控项变化时请求数据
  useEffect(() => {
    if (monitorItemValue !== undefined) {
      for (const index in mointorClassData) {
        const { graphs } = mointorClassData[index]
        const current = graphs.find(
          ({ graphId, title }) => `${graphId}-${title}` === monitorItemValue
        )
        if (current) {
          setCurrentGraph(current)
          setDelayOption(current)
          break
        }
      }
    }
  }, [monitorItemValue])

  // 切换日期范围改变粒度
  const rangeDateChange = (date, quick?) => {
    const [start, end] = date
    if (start && end) {
      setDelayOption(currentGraph, date)
      setQuickDate(quick)
      setRangeDate(date)
    }
  }

  // 设置粒度
  const setDelayOption = (
    graph = currentGraph,
    date = rangeDate?.length ? rangeDate : rangeDateProp
  ) => {
    const [start, end] = date
    if (graph?.lines[0]?.delay && start && end) {
      const options = calcSelectInterval(date, graph?.lines[0]?.delay)
      setDelayOptions(options)
      const findDelay = options.includes(delayValue)
      if (!findDelay) {
        setDelayValue(options[0])
      }
    }
  }

  // 日期/监控项改变时发送请求
  useEffect(() => {
    const [start, end] = rangeDate
    if (visible && start && end && currentGraph?.lines) {
      getChartData()
    } else {
      setChartData(undefined)
    }
  }, [rangeDate, currentGraph?.lines])

  // 获取chartData数据
  const getChartData = async (delay?) => {
    const params = getQueryDataParams(
      currentGraph,
      graph.instanceId,
      rangeDate,
      productType,
      delay || delayValue
    )
    // 专线网关
    if (productType === 17) {
      const result = await request(API.getMetricsData, {
        method: 'POST',
        data: params,
        region
      })
      if (result?.data?.data?.length) {
        const xData = result?.data?.data?.[0]?.datapoints?.map(({ timestamp }) => {
          return moment(timestamp * 1000).format(calcDateFormat(rangeDate, params.interval))
        })
        const series = []
        let maxValue = 0
        let minValue = 0
        result?.data?.data?.forEach((item, index) => {
          const { datapoints, metricDesc } = item
          const symbol = datapoints.find(({ value }) => value === null)
          const seriesObject = {
            name: metricDesc,
            type: 'line',
            smooth: true,
            symbol: datapoints.length > 5 && !symbol ? 'none' : 'emptyCircle',
            data: datapoints.map(({ value }) => value),
            showSymbol: false,
            lineStyle: {
              width: 2
            }
          }
          // 取最大值以及最大单位
          const max = Math.max.apply(null, seriesObject.data)
          maxValue = max > maxValue ? max : maxValue
          const min = Math.min.apply(null, seriesObject.data)
          minValue = min > minValue ? min : minValue
          series.push(seriesObject)
        })
        setChartData({
          xData,
          series,
          maxValue,
          minValue
        })
      } else {
        setChartData(undefined)
      }
    } else {
      const {
        data: { data }
      } = await request(API.getChartData, {
        method: 'POST',
        data: params,
        region
      })
      if (data && data.series && data.series.length) {
        const xData = data.series[0].data.map(([time]) =>
          moment(time * 1000).format(calcDateFormat(rangeDate, delay || delayValue))
        )
        const series = []
        let maxValue = 0
        let minValue = 0
        data.series.forEach(({ data, name }) => {
          const symbol = data.find(([_time, count]) => count === null)
          const seriesObject = {
            name,
            type: 'line',
            symbol: data.length > 5 && !symbol ? 'none' : 'emptyCircle',
            data: data.map(([_time, count]) => count),
            showSymbol: false,
            smooth: true,
            lineStyle: {
              width: 2
            }
          }
          series.push(seriesObject)
          const max = Math.max.apply(null, seriesObject.data)
          maxValue = max > maxValue ? max : maxValue
          const min = Math.min.apply(null, seriesObject.data)
          minValue = min > minValue ? min : minValue
        })
        setChartData({
          xData,
          series,
          maxValue,
          minValue
        })
      } else {
        setChartData(undefined)
      }
    }
  }

  return (
    <Dialog
      mode="destroy"
      value={visible}
      width={1140}
      title={_$('监控详情')}
      b-footer={
        <React.Fragment>
          <Button onClick={closeDetails}>关闭</Button>
        </React.Fragment>
      }
      on$change-value={(c, show) => {
        if (!show) {
          closeDetails()
        }
      }}
    >
      <div className={className}>
        <div className="diabol-filter">
          <p>{_$('监控项')}：</p>
          <Select
            card
            width={200}
            value={monitorItemValue}
            onChange={value => {
              setMonitorItemValue(value)
            }}
          >
            {mointorClassData.length &&
              mointorClassData.map(({ appId, name, graphs }) => (
                <OptionGroup label={name} key={appId}>
                  {graphs?.length &&
                    graphs.map(({ graphId, title }) => (
                      <Option key={`${graphId}-${title}`} value={`${graphId}-${title}`}>
                        {_$(title)}
                      </Option>
                    ))}
                </OptionGroup>
              ))}
          </Select>
          <div className="quick-date">
            <QuickRangeDate
              format="YYYY-MM-DD HH:mm"
              optionalDateRange={90}
              dateDiff={31}
              defautlValue={quickDate || rangeDate}
              changeValue={(date, quick) => {
                rangeDateChange(date, quick)
              }}
              quickRange={['近0.5小时', '近1天', '近3天', '近1周', '近30天']}
              clearable={false}
            />
          </div>
          <div className="delay">
            <p>{_$('粒度')}：</p>
            <Select
              width={100}
              value={delayValue}
              onChange={val => {
                setDelayValue(val)
                getChartData(val)
              }}
            >
              {delayOptions.map(item => (
                <Option value={item} key={item}>
                  {item}s
                </Option>
              ))}
            </Select>
          </div>
        </div>
        <div className="chart-box">
          {chartData?.xData?.length && chartData?.series?.length ? (
            <BaseChart
              options={{
                xAxis: {
                  type: 'category',
                  boundaryGap: false,
                  data: chartData.xData
                },
                series: chartData.series,
                yAxis: {
                  symbol: 'circle',
                  symbolSize: 4,
                  name: currentGraph.units,
                  nameTextStyle: {
                    align: 'left'
                  },
                  type: 'value',
                  axisLine: {
                    show: true
                  },
                  axisLabel: {
                    margin: 5,
                    formatter: function (value, index) {
                      if (value === 0) {
                        return 0
                      }
                      return handleUnit(value, currentGraph.units, true)
                    }
                  }
                },
                dataZoom:
                  chartData.xData?.length > 20
                    ? [
                        {
                          type: 'inside',
                          start: 0,
                          end: 100
                        },
                        {
                          start: 0,
                          end: 100
                        }
                      ]
                    : [],
                legend: {
                  right: '10px',
                  top: '7px',
                  type: 'scroll',
                  width: '80%'
                },
                tooltip: {
                  trigger: 'axis',
                  enterable: true,
                  position: function (point, params, dom, rect, size) {
                    const { contentSize, viewSize } = size
                    let x, y
                    // x
                    if (point[0] > viewSize[0] - contentSize[0]) {
                      x = point[0] - contentSize[0]
                    } else {
                      x = point[0]
                    }
                    // y
                    if (point[1] > viewSize[1] - contentSize[1]) {
                      y = point[1] - contentSize[1]
                    } else {
                      y = point[1]
                    }
                    x = x < 0 ? 0 : x
                    return [x, y]
                  },
                  formatter: function (params) {
                    return tooltipFormatter(params, currentGraph.units)
                  }
                },
                grid: {
                  left: '20px',
                  right: '35px',
                  top: '30px',
                  bottom: '50px',
                  containLabel: true
                }
              }}
            />
          ) : (
            <NoDataComponent />
          )}
        </div>
      </div>
    </Dialog>
  )
}

export default styled(DiabolChartDetails)`
  p {
    margin: 0;
  }
  .diabol-filter {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding-bottom: 8px;
    & > div {
      display: flex;
      align-items: center;
    }
    .quick-date {
      padding-right: 16px;
    }
  }
  .chart-box {
    width: 100%;
    height: 350px;
  }
`
